import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Typography,
  Button,
  List,
  Image,
  Form,
  Input,
  Select,
  Modal,
  message,
  Row,
  Col,
  Divider,
  Alert,
  Space,
  Tag,
  Steps,
  Result
} from "antd";
import {
  ShoppingCartOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
  RightOutlined,
  WhatsAppOutlined
} from "@ant-design/icons";
import { useBasket } from "../components/BasketProvider";

const { Title, Text } = Typography;
const { Option } = Select;
const { Step } = Steps;

// Kenyan counties list
const kenyanCounties = [
  "Nairobi", "Mombasa", "Kwale", "Kilifi", "Tana River", "Lamu",
  "Taita-Taveta", "Garissa", "Wajir", "Mandera", "Marsabit",
  "Isiolo", "Meru", "Tharaka-Nithi", "Embu", "Kitui",
  "Machakos", "Makueni", "Nyandarua", "Nyeri", "Kirinyaga",
  "Murang'a", "Kiambu", "Turkana", "West Pokot", "Samburu",
  "Trans-Nzoia", "Uasin Gishu", "Elgeyo-Marakwet", "Nandi",
  "Baringo", "Laikipia", "Nakuru", "Narok", "Kajiado",
  "Kericho", "Bomet", "Kakamega", "Vihiga", "Bungoma",
  "Busia", "Siaya", "Kisumu", "Homa Bay", "Migori",
  "Kisii", "Nyamira"
];

const Checkout = () => {
  const { basket, dispatch } = useBasket();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const totalAmount = basket.reduce((sum, item) => sum + item.price * item.quantity, 0);

  // Helper to format basket items for WhatsApp message
  const formatOrderMessage = (values) => {
    const itemsList = basket.map(item =>
      `• ${item.title} x ${item.quantity} = Ksh ${(item.price * item.quantity).toFixed(2)}`
    ).join("\n");

    const customerDetails = `
*Customer Information*
Name: ${values.name}
Phone: ${values.phoneNumber}
Email: ${values.email}

*Shipping Address*
House/Street: ${values.house}
Country: ${values.country}
County: ${values.county}
City: ${values.city}

*Order Summary*
${itemsList}

*Total Amount: Ksh ${totalAmount.toFixed(2)}*
    `;

    return customerDetails;
  };

  const handlePlaceOrder = async (values) => {
    if (basket.length === 0) {
      message.warning("Your basket is empty!");
      return;
    }

    setIsPlacingOrder(true);

    try {
      // Prepare WhatsApp message
      const messageBody = formatOrderMessage(values);
      const encodedMessage = encodeURIComponent(messageBody);
      const whatsappUrl = `https://wa.me/254729335354?text=${encodedMessage}`;

      // Open WhatsApp in a new tab
      window.open(whatsappUrl, "_blank");

      // Clear basket after order is sent
      dispatch({ type: "CLEAR_BASKET" });
      form.resetFields();

      // Show success modal
      setIsSuccessModalVisible(true);
    } catch (error) {
      message.error("Something went wrong. Please try again.");
      console.error(error);
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const handleModalClose = () => {
    setIsSuccessModalVisible(false);
    navigate("/");
  };

  const steps = [
    { title: "Your Details", icon: <UserOutlined /> },
    { title: "Shipping", icon: <EnvironmentOutlined /> },
    { title: "Review", icon: <CheckCircleOutlined /> }
  ];

  return (
    <div style={{ background: "#f5f7fb", minHeight: "100vh", padding: "40px 20px" }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <Title level={2} style={{ color: "#2c3e50", marginBottom: "8px" }}>
            <ShoppingCartOutlined /> Secure Checkout
          </Title>
          <Text type="secondary">Complete your order in a few easy steps</Text>
        </div>

        {/* Steps indicator */}
        <div style={{ marginBottom: "40px", maxWidth: "600px", margin: "0 auto 40px" }}>
          <Steps current={currentStep} onChange={setCurrentStep}>
            {steps.map((step, idx) => (
              <Step key={idx} title={step.title} icon={step.icon} />
            ))}
          </Steps>
        </div>

        <Row gutter={[32, 32]}>
          {/* Left Column – Form */}
          <Col xs={24} lg={14}>
            <Card
              bordered={false}
              style={{
                borderRadius: "24px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                overflow: "hidden"
              }}
              bodyStyle={{ padding: "28px" }}
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={handlePlaceOrder}
                onFieldsChange={() => setCurrentStep(0)}
                initialValues={{ country: "Kenya" }}
              >
                {/* Step 1: Personal Info */}
                <div style={{ display: currentStep === 0 ? "block" : "none" }}>
                  <Title level={4} style={{ marginBottom: "24px", color: "#2c3e50" }}>
                    <UserOutlined /> Personal Information
                  </Title>
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="name"
                        label="Full Name"
                        rules={[{ required: true, message: "Please enter your name" }]}
                      >
                        <Input prefix={<UserOutlined />} placeholder="John Doe" size="large" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="phoneNumber"
                        label="Phone Number"
                        rules={[{ required: true, message: "Please enter your phone number" }]}
                      >
                        <Input prefix={<PhoneOutlined />} placeholder="0712345678" size="large" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item
                    name="email"
                    label="Email Address"
                    rules={[
                      { required: true, message: "Please enter your email" },
                      { type: "email", message: "Enter a valid email address" }
                    ]}
                  >
                    <Input prefix={<MailOutlined />} placeholder="you@example.com" size="large" />
                  </Form.Item>
                </div>

                {/* Step 2: Shipping Address */}
                <div style={{ display: currentStep === 1 ? "block" : "none" }}>
                  <Title level={4} style={{ marginBottom: "24px", color: "#2c3e50" }}>
                    <EnvironmentOutlined /> Shipping Address
                  </Title>
                  <Form.Item
                    name="house"
                    label="House/Street Name"
                    rules={[{ required: true, message: "Please enter your house/street" }]}
                  >
                    <Input prefix={<HomeOutlined />} placeholder="123 Main Street" size="large" />
                  </Form.Item>
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item name="country" label="Country">
                        <Input prefix={<GlobalOutlined />} disabled size="large" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="county"
                        label="County"
                        rules={[{ required: true, message: "Please select your county" }]}
                      >
                        <Select placeholder="Select county" size="large" showSearch>
                          {kenyanCounties.map(county => (
                            <Option key={county} value={county}>{county}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item
                    name="city"
                    label="City/Town"
                    rules={[{ required: true, message: "Please enter your city/town" }]}
                  >
                    <Input placeholder="Nairobi" size="large" />
                  </Form.Item>
                </div>

                {/* Step 3: Review & Order */}
                <div style={{ display: currentStep === 2 ? "block" : "none" }}>
                  <Title level={4} style={{ marginBottom: "24px", color: "#2c3e50" }}>
                    <CheckCircleOutlined /> Review Your Order
                  </Title>
                  <Alert
                    message="Order will be sent via WhatsApp"
                    description="After clicking 'Place Order', you will be redirected to WhatsApp to confirm and send your order to our team."
                    type="info"
                    showIcon
                    style={{ marginBottom: "24px", borderRadius: "16px" }}
                    icon={<WhatsAppOutlined />}
                  />
                  <div style={{ background: "#f8f9fa", borderRadius: "20px", padding: "16px", marginBottom: "24px" }}>
                    <Text strong>Order Summary</Text>
                    <div style={{ marginTop: "12px" }}>
                      {basket.map(item => (
                        <div key={item.id} style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                          <Text>{item.title} x {item.quantity}</Text>
                          <Text>Ksh {(item.price * item.quantity).toFixed(2)}</Text>
                        </div>
                      ))}
                      <Divider style={{ margin: "12px 0" }} />
                      <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold" }}>
                        <Text strong>Total</Text>
                        <Text strong style={{ color: "#1890ff", fontSize: "18px" }}>
                          Ksh {totalAmount.toFixed(2)}
                        </Text>
                      </div>
                    </div>
                  </div>

                  <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    loading={isPlacingOrder}
                    block
                    icon={<WhatsAppOutlined />}
                    style={{
                      height: "52px",
                      fontSize: "16px",
                      borderRadius: "40px",
                      background: "#25D366",
                      borderColor: "#25D366",
                      boxShadow: "0 4px 12px rgba(37, 211, 102, 0.3)"
                    }}
                  >
                    {isPlacingOrder ? "Preparing Order..." : "Place Order via WhatsApp"}
                  </Button>
                </div>

                {/* Navigation buttons */}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: "32px" }}>
                  {currentStep > 0 && (
                    <Button onClick={() => setCurrentStep(currentStep - 1)} size="large">
                      Previous
                    </Button>
                  )}
                  {currentStep < 2 && (
                    <Button type="primary" onClick={() => setCurrentStep(currentStep + 1)} size="large">
                      Next <RightOutlined />
                    </Button>
                  )}
                </div>
              </Form>
            </Card>
          </Col>

          {/* Right Column – Order Summary */}
          <Col xs={24} lg={10}>
            <Card
              bordered={false}
              style={{
                borderRadius: "24px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                position: "sticky",
                top: "20px"
              }}
              bodyStyle={{ padding: "24px" }}
            >
              <Title level={4} style={{ marginBottom: "20px" }}>
                <ShoppingCartOutlined /> Your Basket
              </Title>
              {basket.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <Text type="secondary">Your basket is empty</Text>
                  <Button type="link" onClick={() => navigate("/")}>
                    Continue Shopping
                  </Button>
                </div>
              ) : (
                <>
                  <List
                    dataSource={basket}
                    renderItem={item => (
                      <List.Item style={{ padding: "12px 0", borderBottom: "1px solid #f0f0f0" }}>
                        <List.Item.Meta
                          avatar={
                            <Image
                              src={item.image}
                              alt={item.title}
                              width={50}
                              height={50}
                              style={{ borderRadius: "12px", objectFit: "cover" }}
                              preview={false}
                            />
                          }
                          title={<Text strong>{item.title}</Text>}
                          description={
                            <Space>
                              <Tag>Qty: {item.quantity}</Tag>
                              <Text type="secondary">@ Ksh {item.price.toFixed(2)}</Text>
                            </Space>
                          }
                        />
                        <Text strong>Ksh {(item.price * item.quantity).toFixed(2)}</Text>
                      </List.Item>
                    )}
                  />
                  <Divider />
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px" }}>
                    <Text strong style={{ fontSize: "18px" }}>Total</Text>
                    <Text strong style={{ fontSize: "22px", color: "#1890ff" }}>
                      Ksh {totalAmount.toFixed(2)}
                    </Text>
                  </div>
                  <div style={{ marginTop: "24px" }}>
                    <Alert
                      message="Free Delivery"
                      description="We deliver nationwide with no extra shipping cost."
                      type="success"
                      showIcon
                      style={{ borderRadius: "16px" }}
                    />
                  </div>
                </>
              )}
            </Card>
          </Col>
        </Row>

        {/* Success Modal */}
        <Modal
          visible={isSuccessModalVisible}
          footer={null}
          closable={false}
          centered
          bodyStyle={{ padding: "32px", textAlign: "center" }}
          width={480}
        >
          <Result
            status="success"
            title="Order Sent Successfully!"
            subTitle="You've been redirected to WhatsApp. Please send the message to confirm your order. Our team will get back to you shortly."
            extra={[
              <Button type="primary" onClick={handleModalClose} key="continue">
                Continue Shopping
              </Button>
            ]}
          />
        </Modal>
      </div>
    </div>
  );
};

export default Checkout;